import { useEffect, useState } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Card, Typography, Segmented, Row, Col, Statistic, Table, Empty, Spin,
  App as AntApp, Flex, Progress, Tag, Modal, Select, DatePicker, Space, Tooltip, Tabs,
} from 'antd';
import ComparativoAnos from './comercial-dashboard/ComparativoAnos';
import dayjs, { Dayjs } from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import { API_URL } from '../config';
import { fmtDataBr } from '../utils/dataFmt';

const { Title, Text } = Typography;

async function getToken(): Promise<string> {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() ?? '';
}

const fmt = (v: number) => 'R$ ' + (v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmt2 = (v: number) => 'R$ ' + (v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtCompacto = (v: number): string => {
  const n = v || 0;
  if (n >= 1_000_000) return 'R$ ' + (n / 1_000_000).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + 'M';
  if (n >= 1_000)     return 'R$ ' + Math.round(n / 1_000).toLocaleString('pt-BR') + 'k';
  return 'R$ ' + Math.round(n).toLocaleString('pt-BR');
};

type Janela = '12m' | 'ano' | 'trimestre' | 'mes' | 'tudo' | 'ano_especifico' | 'custom';
type Granularidade = 'mes' | 'ano';
type VariavelGrafico = 'receita_ganha' | 'propostas_criadas' | 'valor_proposto' | 'negocios_fechados' | 'taxa_conversao_pct';

// D1.3 (2026-06-03): variáveis selecionáveis no gráfico de barras.
// Cada variável tem um extractor (lê o campo do bucket), um formatador
// pro valor exibido no topo da barra, e um label pra UI.
const VARIAVEIS: Array<{
  key: VariavelGrafico;
  label: string;
  curto: string;
  unidade: 'reais' | 'qtd' | 'percent';
  extrair: (b: any) => number | null;
}> = [
  { key: 'receita_ganha',    label: 'Receita ganha',    curto: 'Receita',    unidade: 'reais',   extrair: (b) => b.receita_ganha ?? b.total ?? 0 },
  { key: 'propostas_criadas',label: 'Propostas criadas',curto: 'Propostas',  unidade: 'qtd',     extrair: (b) => b.propostas_criadas ?? 0 },
  { key: 'valor_proposto',   label: 'Valor proposto',   curto: 'Proposto',   unidade: 'reais',   extrair: (b) => b.valor_proposto ?? 0 },
  { key: 'negocios_fechados',label: 'Negócios fechados',curto: 'Fechados',   unidade: 'qtd',     extrair: (b) => b.negocios_fechados ?? 0 },
  { key: 'taxa_conversao_pct',label: 'Taxa de conversão',curto: 'Conversão', unidade: 'percent', extrair: (b) => b.taxa_conversao_pct ?? null },
];

function fmtVariavel(v: number | null, unidade: 'reais' | 'qtd' | 'percent'): string {
  if (v == null) return '—';
  if (unidade === 'reais') {
    const n = v || 0;
    if (n >= 1_000_000) return 'R$ ' + (n / 1_000_000).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + 'M';
    if (n >= 1_000)     return 'R$ ' + Math.round(n / 1_000).toLocaleString('pt-BR') + 'k';
    return 'R$ ' + Math.round(n).toLocaleString('pt-BR');
  }
  if (unidade === 'percent') return `${v.toFixed(1)}%`;
  return Math.round(v).toLocaleString('pt-BR');
}

const JANELAS = [
  { label: 'Mês atual', value: 'mes' },
  { label: 'Últimos 3 meses', value: 'trimestre' },
  { label: 'Últimos 12 meses', value: '12m' },
  { label: 'Ano corrente', value: 'ano' },
  { label: 'Todo o período', value: 'tudo' },
];

// Gera lista de anos disponíveis (do ano atual até 2020, descendente)
const ANOS_DISPONIVEIS: number[] = (() => {
  const atual = new Date().getFullYear();
  const arr: number[] = [];
  for (let y = atual; y >= 2020; y--) arr.push(y);
  return arr;
})();

interface VendasModalState {
  titulo: string;
  ini: string;  // YYYY-MM-DD
  fim: string;  // YYYY-MM-DD
}

interface VendaItem {
  id: number;
  titulo: string;
  cliente: string | null;
  valor_final: string | number | null;
  valor_estimado: string | number | null;
  data_fechamento: string;
  responsavel: string | null;
  origem: string | null;
  contrato_aditivo_id: string | null;
  contrato_pai_numero: string | null;
}

function ComercialDashboardInner() {
  const [data, setData] = useState<any>(null);
  const [metas, setMetas] = useState<any>(null);
  const [janela, setJanela] = useState<Janela>('12m');
  // Filtros adicionais (só usados quando janela é 'ano_especifico' ou 'custom')
  const [anoEspecifico, setAnoEspecifico] = useState<number>(new Date().getFullYear() - 1);
  const [rangeCustom, setRangeCustom] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  const [granularidade, setGranularidade] = useState<Granularidade>('mes');
  const [variavelGrafico, setVariavelGrafico] = useState<VariavelGrafico>('receita_ganha');
  const [loading, setLoading] = useState(true);
  // Período de cada card de meta (defaults atuais — Lucas pode alterar nos selects)
  const hojeRef = new Date();
  const [cardMesPeriodo, setCardMesPeriodo] = useState<{ ano: number; mes: number }>({
    ano: hojeRef.getFullYear(), mes: hojeRef.getMonth(),
  });
  const [cardTriPeriodo, setCardTriPeriodo] = useState<{ ano: number; tri: number }>({
    ano: hojeRef.getFullYear(), tri: Math.floor(hojeRef.getMonth() / 3),
  });
  const [cardAnoPeriodo, setCardAnoPeriodo] = useState<{ ano: number }>({
    ano: hojeRef.getFullYear(),
  });
  const [vendasModal, setVendasModal] = useState<VendasModalState | null>(null);
  const [vendasModalData, setVendasModalData] = useState<{ total: number; vendas: VendaItem[] } | null>(null);
  const [vendasModalLoading, setVendasModalLoading] = useState(false);

  useEffect(() => { carregar(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [janela, anoEspecifico, rangeCustom, granularidade]);

  // Carrega vendas do período quando o modal abre
  useEffect(() => {
    if (!vendasModal) { setVendasModalData(null); return; }
    (async () => {
      setVendasModalLoading(true);
      setVendasModalData(null);
      try {
        const token = await getToken();
        const res = await axios.get(
          `${API_URL}/comercial/dashboard/vendas?ini=${vendasModal.ini}&fim=${vendasModal.fim}`,
          { headers: { Authorization: token } });
        const d = res.data.body ? JSON.parse(res.data.body) : res.data;
        setVendasModalData({
          total: parseFloat(d.total) || 0,
          vendas: Array.isArray(d.vendas) ? d.vendas : [],
        });
      } catch (e) {
        console.error(e);
      } finally { setVendasModalLoading(false); }
    })();
  }, [vendasModal]);

  // Helpers de período pra clicks
  function ultimoDiaMes(ano: number, mes0: number) {
    return new Date(ano, mes0 + 1, 0).getDate();
  }
  function periodoMes(periodoYM: string): VendasModalState {
    // Aceita 'YYYY' (ano inteiro) ou 'YYYY-MM' (mês específico)
    if (/^\d{4}$/.test(periodoYM)) {
      return {
        titulo: `Vendas de ${periodoYM}`,
        ini: `${periodoYM}-01-01`,
        fim: `${periodoYM}-12-31`,
      };
    }
    const [y, m] = periodoYM.split('-').map(Number);
    return {
      titulo: `Vendas de ${formatarMes(periodoYM)}`,
      ini: `${periodoYM}-01`,
      fim: `${periodoYM}-${String(ultimoDiaMes(y, m - 1)).padStart(2, '0')}`,
    };
  }

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      // Monta query string com base na janela escolhida
      const params: string[] = [`granularidade=${granularidade}`];
      if (janela === 'custom' && rangeCustom[0] && rangeCustom[1]) {
        params.push(`ini=${rangeCustom[0].format('YYYY-MM-DD')}`);
        params.push(`fim=${rangeCustom[1].format('YYYY-MM-DD')}`);
      } else if (janela === 'ano_especifico') {
        params.push(`ano=${anoEspecifico}`);
      } else {
        params.push(`janela=${janela}`);
      }
      // Metas: calcula range de anos com base na janela escolhida.
      // - 'ano_especifico' → só esse ano
      // - 'custom' → todos os anos do range YYYY-MM-DD
      // - '12m' → ano corrente + anterior (12m pode cobrir 2 anos)
      // - 'tudo' → últimos 5 anos (limite pra não abusar)
      // - default → ano corrente
      const anoCorrente = new Date().getFullYear();
      let anosMetas: number[] = [];
      if (janela === 'ano_especifico') {
        anosMetas = [anoEspecifico];
      } else if (janela === 'custom' && rangeCustom[0] && rangeCustom[1]) {
        const yi = rangeCustom[0].year();
        const yf = rangeCustom[1].year();
        for (let y = yi; y <= yf; y++) anosMetas.push(y);
      } else if (janela === '12m') {
        anosMetas = [anoCorrente - 1, anoCorrente];
      } else if (janela === 'tudo') {
        for (let y = anoCorrente - 4; y <= anoCorrente; y++) anosMetas.push(y);
      } else {
        anosMetas = [anoCorrente];
      }
      // Sempre garante o ano corrente (cards Mês/Trim/Ano precisam dele)
      if (!anosMetas.includes(anoCorrente)) anosMetas.push(anoCorrente);

      const reqsMetas = anosMetas.map(a =>
        axios.get(`${API_URL}/comercial/metas?ano=${a}`, { headers: { Authorization: token } })
          .catch(() => null)
      );
      const [r1, ...rsMetas] = await Promise.all([
        axios.get(`${API_URL}/comercial/dashboard?${params.join('&')}`, { headers: { Authorization: token } }),
        ...reqsMetas,
      ]);
      const d = r1.data.body ? JSON.parse(r1.data.body) : r1.data;
      setData(d);
      // Merge metas dos múltiplos anos em 1 objeto { meses: [...] }
      const todasMeses: any[] = [];
      for (const r of rsMetas) {
        if (!r) continue;
        const m = r.data.body ? JSON.parse(r.data.body) : r.data;
        if (m.meses) todasMeses.push(...m.meses);
      }
      setMetas({ meses: todasMeses });
    } catch (e: any) {
      console.error(e);
    } finally { setLoading(false); }
  }

  // Mapa de meta por mês (YYYY-MM → meta_valor) pra overlay no gráfico
  const metaPorMes: Record<string, number> = {};
  const realizadoPorMes: Record<string, number> = {};
  if (metas?.meses) {
    metas.meses.forEach((m: any) => {
      metaPorMes[m.periodo] = m.meta_valor || 0;
      realizadoPorMes[m.periodo] = m.realizado || 0;
    });
  }

  // ─── Pro-rata helpers: calcula meta esperada até HOJE dentro do período ───
  // Lógica: se hoje está no dia 15 de um mês de 30 dias, esperamos 50% da
  // meta mensal. Cor do semáforo compara realizado vs pro-rata (não vs total).
  // % atingimento exibido continua sendo realizado/meta_total (sem pro-rata).
  const hoje = new Date();
  const anoAtual = hoje.getFullYear();
  const mesAtual = hoje.getMonth();  // 0-indexed
  const triAtual = Math.floor(mesAtual / 3);

  function bucketPeriodo(opts: { ano: number; mesInicio: number; mesFim: number }) {
    // Soma meta + realizado dos meses no range [mesInicio..mesFim] (0-indexed)
    let meta = 0, real = 0;
    for (let m = opts.mesInicio; m <= opts.mesFim; m++) {
      const periodo = `${opts.ano}-${String(m + 1).padStart(2, '0')}`;
      meta += metaPorMes[periodo] || 0;
      real += realizadoPorMes[periodo] || 0;
    }
    return { meta, real };
  }

  function proRataPeriodo(opts: { dtIni: Date; dtFim: Date }) {
    // Fração [0..1] do período que já passou. Se já terminou, retorna 1.
    // Se ainda não começou, retorna 0.
    const ms = opts.dtFim.getTime() - opts.dtIni.getTime();
    if (ms <= 0) return 0;
    const passou = hoje.getTime() - opts.dtIni.getTime();
    return Math.max(0, Math.min(1, passou / ms));
  }

  // Constrói os 3 cards baseado nos states (Lucas pode alterar mês/trim/ano)
  const mes = bucketPeriodo({ ano: cardMesPeriodo.ano, mesInicio: cardMesPeriodo.mes, mesFim: cardMesPeriodo.mes });
  const tri = bucketPeriodo({ ano: cardTriPeriodo.ano, mesInicio: cardTriPeriodo.tri * 3, mesFim: cardTriPeriodo.tri * 3 + 2 });
  const ano = bucketPeriodo({ ano: cardAnoPeriodo.ano, mesInicio: 0, mesFim: 11 });

  const proRataMes = proRataPeriodo({
    dtIni: new Date(cardMesPeriodo.ano, cardMesPeriodo.mes, 1),
    dtFim: new Date(cardMesPeriodo.ano, cardMesPeriodo.mes + 1, 1),
  });
  const proRataTri = proRataPeriodo({
    dtIni: new Date(cardTriPeriodo.ano, cardTriPeriodo.tri * 3, 1),
    dtFim: new Date(cardTriPeriodo.ano, cardTriPeriodo.tri * 3 + 3, 1),
  });
  const proRataAno = proRataPeriodo({
    dtIni: new Date(cardAnoPeriodo.ano, 0, 1),
    dtFim: new Date(cardAnoPeriodo.ano + 1, 0, 1),
  });

  interface MetaCard {
    label: string;
    meta: number;
    real: number;
    proRata: number;       // [0..1] fração do período passado
    metaEsperada: number;  // meta * proRata
    pctTotal: number | null;  // real/meta (mostrado no card)
    statusCor: string;     // verde / amarelo / vermelho
    statusLabel: string;
  }

  function montarCard(label: string, b: { meta: number; real: number }, pr: number): MetaCard {
    const metaEsperada = b.meta * pr;
    const pctTotal = b.meta > 0 ? Math.round((b.real / b.meta) * 100) : null;
    // Status: se já bateu meta total → verde. Senão compara com pro-rata.
    let statusCor = '#888';
    let statusLabel = 'sem meta';
    if (b.meta > 0) {
      if (b.real >= b.meta) {
        statusCor = '#0a7f4a'; statusLabel = '✓ Meta batida';
      } else if (metaEsperada === 0) {
        // Período ainda não começou
        statusCor = '#888'; statusLabel = 'aguardando início';
      } else if (b.real >= metaEsperada) {
        statusCor = '#0a7f4a'; statusLabel = '✓ No rumo';
      } else if (b.real >= metaEsperada * 0.7) {
        statusCor = '#B07500'; statusLabel = '⚠ Atrás do esperado';
      } else {
        statusCor = '#B91626'; statusLabel = '✗ Abaixo da meta';
      }
    }
    return { label, ...b, proRata: pr, metaEsperada, pctTotal, statusCor, statusLabel };
  }

  const MESES_CURTOS = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
  const labelMes = cardMesPeriodo.ano === anoAtual && cardMesPeriodo.mes === mesAtual
    ? 'Mês atual'
    : `${MESES_CURTOS[cardMesPeriodo.mes]}/${String(cardMesPeriodo.ano).slice(2)}`;
  const labelTri = cardTriPeriodo.ano === anoAtual && cardTriPeriodo.tri === triAtual
    ? `${triAtual + 1}º Trimestre`
    : `${cardTriPeriodo.tri + 1}º Tri ${cardTriPeriodo.ano}`;
  const labelAno = `Ano ${cardAnoPeriodo.ano}`;
  const cardMes = montarCard(labelMes, mes, proRataMes);
  const cardTri = montarCard(labelTri, tri, proRataTri);
  const cardAno = montarCard(labelAno, ano, proRataAno);
  const cardsMeta = [cardMes, cardTri, cardAno];

  // Mostra row de cards se HÁ ALGUMA meta cadastrada em qualquer ano carregado
  // (não só nos períodos selecionados — usuário precisa do select pra navegar).
  const temMetaCadastrada = (metas?.meses || []).some((m: any) => (m.meta_valor || 0) > 0);
  const pctAtingimentoAno = cardAno.pctTotal;

  // D1.3 (2026-06-03): variável selecionável + média móvel 3 buckets.
  const varConfig = VARIAVEIS.find(v => v.key === variavelGrafico) || VARIAVEIS[0];
  const valoresSerie: Array<number | null> = (data?.serie_mensal || []).map((m: any) => varConfig.extrair(m));
  // Média móvel 3 buckets: aritmética simples; ignora bucket sem valor.
  // Só desenha quando ≥ 3 buckets — abaixo disso a linha vira ruído visual.
  const mediaMovel: Array<number | null> = valoresSerie.map((_, i) => {
    if (i < 2 || valoresSerie.length < 3) return null;
    const janelaBucket = valoresSerie.slice(i - 2, i + 1).filter((x): x is number => x != null);
    if (janelaBucket.length === 0) return null;
    return janelaBucket.reduce((a, b) => a + b, 0) / janelaBucket.length;
  });
  // Max do gráfico: maior valor real + meta (só na variável "receita_ganha")
  const valoresPraEscala: number[] = [];
  valoresSerie.forEach(v => { if (v != null) valoresPraEscala.push(v); });
  mediaMovel.forEach(v => { if (v != null) valoresPraEscala.push(v); });
  if (variavelGrafico === 'receita_ganha') {
    (data?.serie_mensal || []).forEach((m: any) => {
      const meta = metaPorMes[m.mes] || 0;
      if (meta > 0) valoresPraEscala.push(meta);
    });
  }
  const maxSerie = valoresPraEscala.length ? Math.max(...valoresPraEscala) : 0;

  // Cellrenderer reutilizável: formato compacto (R$ X,XM / Xk) + nowrap pra
  // não quebrar coluna estreita. Tooltip mostra valor completo (R$ X.XXX,XX).
  const renderValor = (v: number, cor = '#000') => (
    <Text strong style={{ color: cor, whiteSpace: 'nowrap' }} title={fmt(v)}>
      {fmtCompacto(v)}
    </Text>
  );

  const colsFunil: ColumnsType<any> = [
    { title: 'Status', dataIndex: 'status', key: 'st', ellipsis: true, render: (s) => labelStatus(s) },
    { title: 'Qtd', dataIndex: 'n', key: 'n', align: 'right', width: 50 },
    { title: 'Valor', dataIndex: 'valor', key: 'v', align: 'right', width: 90, render: (v) => renderValor(v) },
  ];

  const colsVendedores: ColumnsType<any> = [
    { title: 'Responsável', dataIndex: 'responsavel', key: 'r', ellipsis: true },
    { title: 'Qtd', dataIndex: 'n', key: 'n', align: 'right', width: 50 },
    { title: 'Total', dataIndex: 'total', key: 't', align: 'right', width: 90, render: (v) => renderValor(v, '#0a7f4a') },
  ];

  const colsClientes: ColumnsType<any> = [
    { title: 'Cliente', dataIndex: 'razao_social', key: 'c', ellipsis: true },
    { title: 'Qtd', dataIndex: 'n', key: 'n', align: 'right', width: 50 },
    { title: 'Total', dataIndex: 'total', key: 't', align: 'right', width: 90, render: (v) => renderValor(v, '#0a7f4a') },
  ];

  return (
    <div style={{ padding: '16px 24px' }}>
      <Flex justify="space-between" align="start" wrap="wrap" gap={12} style={{ marginBottom: 18 }}>
        <div>
          <Title level={3} style={{ margin: 0, color: '#020D7F' }}>Dashboard Comercial</Title>
          {data?.data_inicio && data?.data_fim && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              Período: {fmtDataBr(data.data_inicio)} → {fmtDataBr(data.data_fim)}
              {' '}· Granularidade: {data.granularidade === 'ano' ? 'anual' : 'mensal'}
            </Text>
          )}
        </div>
        <Space direction="vertical" size={6} align="end">
          {/* Linha 1: presets de janela */}
          <Segmented
            value={janela === 'ano_especifico' || janela === 'custom' ? '12m' : janela}
            onChange={(v) => setJanela(v as Janela)}
            options={JANELAS}
            size="small"
          />
          {/* Linha 2: ano específico + custom + granularidade */}
          <Space size={6} wrap>
            <Select size="small" style={{ width: 130 }}
              placeholder="Ano específico"
              value={janela === 'ano_especifico' ? anoEspecifico : undefined}
              onChange={(v) => { setAnoEspecifico(v); setJanela('ano_especifico'); }}
              options={ANOS_DISPONIVEIS.map(y => ({ value: y, label: `Ano ${y}` }))}
              allowClear
              onClear={() => setJanela('12m')} />
            <DatePicker.RangePicker size="small" style={{ width: 240 }}
              format="DD/MM/YYYY"
              placeholder={['Personalizado de', 'até']}
              value={janela === 'custom' ? rangeCustom : [null, null]}
              onChange={(v) => {
                if (v && v[0] && v[1]) {
                  setRangeCustom([v[0], v[1]]);
                  setJanela('custom');
                } else {
                  setRangeCustom([null, null]);
                  if (janela === 'custom') setJanela('12m');
                }
              }} />
            <Segmented size="small"
              value={granularidade}
              onChange={(v) => setGranularidade(v as Granularidade)}
              options={[
                { label: 'Mensal', value: 'mes' },
                { label: 'Anual', value: 'ano' },
              ]} />
          </Space>
        </Space>
      </Flex>

      {loading ? (
        <Flex justify="center" align="center" style={{ minHeight: 200 }}><Spin /></Flex>
      ) : !data ? (
        <Empty description="Erro ao carregar dados" />
      ) : (
        <>
          {/* 3 cards de Meta: Mês / Trimestre / Ano — com semáforo pro-rata.
              Selects dentro de cada card permitem alterar o período de análise. */}
          {temMetaCadastrada && (
            <Row gutter={[12, 12]} style={{ marginBottom: 14 }}>
              {cardsMeta.map((c, idx) => {
                const pctBar = c.meta > 0 ? Math.min((c.real / c.meta) * 100, 100) : 0;
                const pctProRata = c.meta > 0 ? Math.round((c.proRata) * 100) : 0;
                // Range de datas dependendo do card e do state escolhido
                const dtIni = idx === 0 ? new Date(cardMesPeriodo.ano, cardMesPeriodo.mes, 1)
                  : idx === 1 ? new Date(cardTriPeriodo.ano, cardTriPeriodo.tri * 3, 1)
                  : new Date(cardAnoPeriodo.ano, 0, 1);
                const dtFimExcl = idx === 0 ? new Date(cardMesPeriodo.ano, cardMesPeriodo.mes + 1, 1)
                  : idx === 1 ? new Date(cardTriPeriodo.ano, cardTriPeriodo.tri * 3 + 3, 1)
                  : new Date(cardAnoPeriodo.ano + 1, 0, 1);
                const dtFim = new Date(dtFimExcl.getTime() - 86400000);
                const ymdIni = `${dtIni.getFullYear()}-${String(dtIni.getMonth()+1).padStart(2,'0')}-${String(dtIni.getDate()).padStart(2,'0')}`;
                const ymdFim = `${dtFim.getFullYear()}-${String(dtFim.getMonth()+1).padStart(2,'0')}-${String(dtFim.getDate()).padStart(2,'0')}`;

                // Seletor de período do card (mes/trim/ano + ano)
                const seletor = idx === 0 ? (
                  <Space size={4} onClick={(e) => e.stopPropagation()}>
                    <Select size="small" value={cardMesPeriodo.mes}
                      onChange={(v) => setCardMesPeriodo({ ...cardMesPeriodo, mes: v })}
                      style={{ width: 78 }}
                      options={MESES_CURTOS.map((m, i) => ({ value: i, label: m }))} />
                    <Select size="small" value={cardMesPeriodo.ano}
                      onChange={(v) => setCardMesPeriodo({ ...cardMesPeriodo, ano: v })}
                      style={{ width: 76 }}
                      options={ANOS_DISPONIVEIS.map(a => ({ value: a, label: String(a) }))} />
                  </Space>
                ) : idx === 1 ? (
                  <Space size={4} onClick={(e) => e.stopPropagation()}>
                    <Select size="small" value={cardTriPeriodo.tri}
                      onChange={(v) => setCardTriPeriodo({ ...cardTriPeriodo, tri: v })}
                      style={{ width: 70 }}
                      options={[0,1,2,3].map(t => ({ value: t, label: `T${t+1}` }))} />
                    <Select size="small" value={cardTriPeriodo.ano}
                      onChange={(v) => setCardTriPeriodo({ ...cardTriPeriodo, ano: v })}
                      style={{ width: 76 }}
                      options={ANOS_DISPONIVEIS.map(a => ({ value: a, label: String(a) }))} />
                  </Space>
                ) : (
                  <Space size={4} onClick={(e) => e.stopPropagation()}>
                    <Select size="small" value={cardAnoPeriodo.ano}
                      onChange={(v) => setCardAnoPeriodo({ ano: v })}
                      style={{ width: 90 }}
                      options={ANOS_DISPONIVEIS.map(a => ({ value: a, label: String(a) }))} />
                  </Space>
                );

                return (
                  <Col key={idx} xs={24} md={8}>
                    <Card size="small" hoverable
                      onClick={() => setVendasModal({ titulo: `Vendas — ${c.label}`, ini: ymdIni, fim: ymdFim })}
                      style={{
                        borderTop: `3px solid ${c.statusCor}`,
                        borderRadius: 6, cursor: 'pointer',
                      }} bodyStyle={{ padding: 12 }}>
                      <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
                        <Text strong style={{ color: '#020D7F', fontSize: 12 }}>🎯 {c.label}</Text>
                        {seletor}
                        <Tag color={
                          c.statusCor === '#0a7f4a' ? 'green'
                          : c.statusCor === '#B07500' ? 'orange'
                          : c.statusCor === '#B91626' ? 'red' : 'default'
                        } style={{ fontSize: 10, margin: 0, padding: '0 6px', lineHeight: '16px' }}>
                          {c.statusLabel}
                        </Tag>
                      </Flex>
                      {c.meta > 0 ? (
                        <>
                          <div style={{ marginTop: 8 }}>
                            <Text style={{ fontSize: 20, fontWeight: 700, color: c.statusCor }}>
                              {c.pctTotal}%
                            </Text>
                            <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>
                              {fmt(c.real)} / {fmt(c.meta)}
                            </Text>
                          </div>
                          <Progress percent={pctBar} size="small" showInfo={false}
                            strokeColor={c.statusCor} style={{ marginTop: 6, marginBottom: 4 }} />
                          {c.metaEsperada > 0 && (
                            <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>
                              Esperado até hoje ({pctProRata}% do período): <strong>{fmt(c.metaEsperada)}</strong>
                              {c.real < c.metaEsperada && c.real < c.meta && (
                                <> · faltam <strong style={{ color: '#B91626' }}>{fmt(c.metaEsperada - c.real)}</strong></>
                              )}
                            </Text>
                          )}
                        </>
                      ) : (
                        <div style={{ marginTop: 8 }}>
                          <Text type="secondary" italic style={{ fontSize: 11 }}>
                            Sem meta cadastrada. Vai em <strong>Comercial → Metas</strong>.
                          </Text>
                        </div>
                      )}
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}

          {/* D1.2 (2026-06-03): termômetro de atividade — sempre ancorado em hoje,
              com PRO-RATA: compara dias 1-X do mês atual vs dias 1-X dos meses
              de referência. Usa MEDIANA 3m (robusta a outliers como o pico de
              abril/2026 com sync retroativo Ploomes). Tooltips explicam o cálculo. */}
          {data.termometro && (
            <Row gutter={[12, 12]} style={{ marginBottom: 14 }}>
              {[
                { ...data.termometro.propostas_criadas, label: 'Propostas criadas', icone: '📤', unidade: 'qtd' as const, breakdownField: 'n' as const },
                { ...data.termometro.valor_proposto, label: 'Valor proposto', icone: '💰', unidade: 'reais' as const, breakdownField: 'valor' as const },
              ].map((c, idx) => {
                const fmtV = (v: number | null) => fmtVariavel(v, c.unidade);
                const dia = data.termometro.dia_atual;
                const breakdownRaw = data.termometro.propostas_criadas.breakdown_3m as Array<{mes: string; n: number; valor: number}>;
                const breakdownField = c.breakdownField as 'n' | 'valor';
                const breakdown3m = breakdownRaw?.map(b => ({ mes: b.mes, valor: b[breakdownField] as number }));
                const anoPassadoDetalhe = data.termometro.ano_passado_detalhe as Array<{cliente: string; titulo: string; valor: number}> | undefined;
                const renderDelta = (pct: number | null | undefined, ref: string, tooltip?: React.ReactNode) => {
                  if (pct == null) return <Text type="secondary" style={{ fontSize: 10 }}>sem base ({ref})</Text>;
                  const cor = pct >= 5 ? '#0a7f4a' : pct <= -5 ? '#B91626' : '#B07500';
                  const seta = pct > 0 ? '▲' : pct < 0 ? '▼' : '▬';
                  const sinal = pct > 0 ? '+' : '';
                  const node = (
                    <Text style={{ fontSize: 11, color: cor, fontWeight: 600, cursor: tooltip ? 'help' : 'default' }}>
                      {seta} {sinal}{pct.toFixed(1)}% <Text type="secondary" style={{ fontSize: 10, fontWeight: 400 }}>vs {ref}</Text>
                    </Text>
                  );
                  return tooltip ? <Tooltip title={tooltip} placement="left">{node}</Tooltip> : node;
                };
                // Tooltip do "vs média 3m": 3 meses individuais + mediana como info secundária
                const tooltip3m = breakdown3m ? (() => {
                  const ordenados = [...breakdown3m].sort((a, b) => a.valor - b.valor);
                  return (
                    <div style={{ fontSize: 12 }}>
                      <div style={{ marginBottom: 4 }}>Média dos primeiros {dia} dias de cada mês:</div>
                      {breakdown3m.map(b => (
                        <div key={b.mes}>• {formatarMes(b.mes)}: <strong>{fmtV(b.valor)}</strong></div>
                      ))}
                      <div style={{ marginTop: 4, paddingTop: 4, borderTop: '1px solid rgba(255,255,255,0.3)' }}>
                        Soma ÷ 3 = média <strong>{fmtV(c.media_3m)}</strong>
                      </div>
                      <div style={{ fontSize: 10, opacity: 0.8, marginTop: 2 }}>
                        Mediana (valor central <span style={{ color: '#FFD700' }}>{fmtV(ordenados[1].valor)}</span>) seria <strong>{fmtV(c.mediana_3m)}</strong> — útil quando há outlier.
                      </div>
                    </div>
                  );
                })() : null;
                // Tooltip do "vs ano passado": lista propostas que formam o total
                const tooltipAno = anoPassadoDetalhe && anoPassadoDetalhe.length > 0 ? (
                  <div style={{ fontSize: 12, maxWidth: 320 }}>
                    <div style={{ marginBottom: 4 }}>
                      {anoPassadoDetalhe.length} proposta(s) nos primeiros {dia} dias do mesmo mês ano passado:
                    </div>
                    {anoPassadoDetalhe.map((p, i) => (
                      <div key={i} style={{ marginBottom: 2 }}>
                        • <strong>{fmtVariavel(p.valor, 'reais')}</strong> — {p.cliente}
                      </div>
                    ))}
                    <div style={{ marginTop: 4, paddingTop: 4, borderTop: '1px solid rgba(255,255,255,0.3)', fontSize: 11 }}>
                      Total: <strong>{fmtV(c.ano_passado)}</strong>
                    </div>
                  </div>
                ) : null;
                return (
                  <Col key={idx} xs={24} md={12}>
                    <Card size="small" style={{ borderLeft: '4px solid #0930D0', borderRadius: 6 }} bodyStyle={{ padding: 14 }}>
                      <Flex justify="space-between" align="start" wrap="wrap" gap={6}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <Text strong style={{ color: '#020D7F', fontSize: 12 }}>
                            {c.icone} {c.label} <Text type="secondary" style={{ fontSize: 11, fontWeight: 400 }}>(até dia {dia})</Text>
                          </Text>
                          <div style={{ fontSize: 26, fontWeight: 700, color: '#0930D0', marginTop: 4, lineHeight: 1.1 }}>
                            {fmtV(c.mes_atual)}
                          </div>
                        </div>
                        <Space direction="vertical" size={2} align="end">
                          {renderDelta(c.delta_vs_3m_pct, `média 3m (${fmtV(c.media_3m)}/mês)`, tooltip3m)}
                          {renderDelta(c.delta_vs_ano_pct, `mesmo período ano passado (${fmtV(c.ano_passado)})`, tooltipAno)}
                        </Space>
                      </Flex>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}

          <Row gutter={[10, 10]} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={12} md={8} lg={6} xl={5}>
              <Card size="small" style={{ background: '#0a7f4a', color: '#fff', border: 'none' }}>
                <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Receita ganha</Text>
                <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>{fmt(data.receita.total)}</div>
                <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 11 }}>{data.receita.n} negócio(s) ganho(s)</Text>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6} xl={5}>
              <Card size="small">
                <Statistic title="Pipeline ativo" value={fmt(data.pipeline_ativo.total)} valueStyle={{ color: '#0930D0', fontSize: 22 }} />
                <Text type="secondary" style={{ fontSize: 11 }}>{data.pipeline_ativo.n} aberto(s)</Text>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6} xl={5}>
              <Card size="small">
                <Statistic title="Ticket médio" value={fmt(data.receita.ticket_medio)} valueStyle={{ color: '#02B5DB', fontSize: 22 }} />
                <Text type="secondary" style={{ fontSize: 11 }}>por negócio ganho</Text>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6} xl={5}>
              <Card size="small">
                <Statistic title="Win rate" value={data.win_rate_pct} precision={1} suffix="%" valueStyle={{ color: '#7C3AED', fontSize: 22 }} />
                <Text type="secondary" style={{ fontSize: 11 }}>{data.ganhos} ganhos · {data.perdidos} perdidos</Text>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6} xl={4}>
              <Card size="small">
                <Statistic title="Ciclo médio" value={data.ciclo_medio_dias} precision={0} suffix=" dias" valueStyle={{ color: '#B07500', fontSize: 22 }} />
                <Text type="secondary" style={{ fontSize: 11 }}>da criação até o ganho</Text>
              </Card>
            </Col>
          </Row>

          <Card
            size="small"
            title={<Text strong style={{ color: '#020D7F' }}>
              {varConfig.label} {granularidade === 'ano' ? '(por ano)' : '(por mês)'} — {tituloJanela(janela)}
            </Text>}
            extra={
              <Segmented
                size="small"
                value={variavelGrafico}
                onChange={(v) => setVariavelGrafico(v as VariavelGrafico)}
                options={VARIAVEIS.map(v => ({ label: v.curto, value: v.key }))}
              />
            }
            style={{ marginBottom: 12 }}
          >
            {data.serie_mensal.length === 0 ? (
              <Empty description={`Sem dados ${tituloJanela(janela).toLowerCase()}`} />
            ) : (<>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 240,
                padding: '20px 4px 0', overflow: 'auto', minWidth: 0, position: 'relative' }}>
                {data.serie_mensal.map((m: any, idx: number) => {
                  const valor = valoresSerie[idx];
                  const h = (valor != null && maxSerie > 0) ? (valor / maxSerie) * 180 : 0;
                  // Linha de meta: só em granularidade mensal E só pra receita_ganha.
                  const meta = granularidade === 'mes' && variavelGrafico === 'receita_ganha'
                    ? (metaPorMes[m.mes] || 0) : 0;
                  const hMeta = maxSerie > 0 && meta > 0 ? (meta / maxSerie) * 180 : 0;
                  const pct = meta > 0 && valor != null ? Math.round((valor / meta) * 100) : null;
                  // Média móvel 3 períodos: só desenha a partir do 3º bucket.
                  const med = mediaMovel[idx];
                  const hMed = (med != null && maxSerie > 0) ? (med / maxSerie) * 180 : 0;
                  return (
                    <div key={m.mes}
                      onClick={() => setVendasModal(periodoMes(m.mes))}
                      style={{ display: 'flex', flexDirection: 'column',
                      alignItems: 'center', flex: 1, minWidth: 64, position: 'relative',
                      cursor: 'pointer' }}
                      title={`Click pra ver vendas de ${formatarMes(m.mes)}`}>
                      <div style={{ fontSize: 10, color: '#444', marginBottom: 4,
                        fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {valor != null && valor !== 0 ? fmtVariavel(valor, varConfig.unidade) : '—'}
                      </div>
                      <div style={{ position: 'relative', width: '100%', maxWidth: 56, height: 180 }}>
                        {/* Barra realizado (variável selecionada) */}
                        <div style={{
                          position: 'absolute', bottom: 0, left: 0, right: 0,
                          height: h, background: 'linear-gradient(180deg, #0930D0 0%, #02B5DB 100%)',
                          borderRadius: '4px 4px 0 0', minHeight: valor != null && valor > 0 ? 2 : 0,
                        }} title={`${m.mes}: ${fmtVariavel(valor, varConfig.unidade)}${meta > 0 ? ` · meta ${fmtCompacto(meta)} (${pct}%)` : ''}${med != null ? ` · média 3p: ${fmtVariavel(med, varConfig.unidade)}` : ''}`} />
                        {/* Linha tracejada da meta — só receita ganha */}
                        {hMeta > 0 && (
                          <div style={{
                            position: 'absolute', bottom: hMeta, left: -4, right: -4,
                            borderTop: '2px dashed #B91626',
                            zIndex: 2,
                          }} title={`Meta: ${fmtCompacto(meta)}`} />
                        )}
                        {/* Linha pontilhada da média móvel 3 períodos */}
                        {hMed > 0 && (
                          <div style={{
                            position: 'absolute', bottom: hMed, left: -4, right: -4,
                            borderTop: '2px dotted #B07500',
                            zIndex: 1,
                          }} title={`Média móvel 3p: ${fmtVariavel(med, varConfig.unidade)}`} />
                        )}
                      </div>
                      <div style={{ fontSize: 10, color: '#888', marginTop: 6 }}>{formatarMes(m.mes)}</div>
                      {pct != null && (
                        <Tag color={pct >= 100 ? 'green' : pct >= 70 ? 'orange' : 'red'}
                          style={{ fontSize: 9, padding: '0 4px', marginTop: 2, lineHeight: '14px' }}>
                          {pct}%
                        </Tag>
                      )}
                    </div>
                  );
                })}
              </div>
              {/* Legenda */}
              <div style={{ fontSize: 10, color: '#666', marginTop: 8, textAlign: 'right' }}>
                <span style={{ display: 'inline-block', width: 12, height: 8, background: 'linear-gradient(180deg, #0930D0, #02B5DB)', verticalAlign: 'middle', marginRight: 4, borderRadius: 2 }} /> {varConfig.curto}
                {data.serie_mensal.length >= 3 && (
                  <>
                    &nbsp;&nbsp;
                    <span style={{ display: 'inline-block', width: 12, borderTop: '2px dotted #B07500', verticalAlign: 'middle', marginRight: 4 }} /> Média móvel 3p
                  </>
                )}
                {variavelGrafico === 'receita_ganha' && Object.values(metaPorMes).some(v => v > 0) && (
                  <>
                    &nbsp;&nbsp;
                    <span style={{ display: 'inline-block', width: 12, borderTop: '2px dashed #B91626', verticalAlign: 'middle', marginRight: 4 }} /> Meta
                  </>
                )}
              </div>
            </>)}
          </Card>

          <Row gutter={[12, 12]}>
            <Col xs={24} md={8}>
              <Card size="small" title={<Text strong style={{ color: '#020D7F' }}>Funil — visão atual</Text>}>
                {data.funil.length === 0 ? <Empty description="Sem dados" /> : (
                  <Table size="small" pagination={false} columns={colsFunil} dataSource={data.funil} rowKey="status" />
                )}
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card size="small" title={<Text strong style={{ color: '#020D7F' }}>Top vendedores</Text>}>
                {data.top_vendedores.length === 0 ? <Empty description="Sem dados" /> : (
                  <Table size="small" pagination={false} columns={colsVendedores} dataSource={data.top_vendedores}
                    rowKey={(_, i) => String(i)} />
                )}
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card size="small" title={<Text strong style={{ color: '#020D7F' }}>Top clientes</Text>}>
                {data.top_clientes.length === 0 ? <Empty description="Sem dados" /> : (
                  <Table size="small" pagination={false} columns={colsClientes} dataSource={data.top_clientes}
                    rowKey={(_, i) => String(i)} />
                )}
              </Card>
            </Col>
          </Row>
        </>
      )}

      {/* Modal drill-down de vendas (clicado num card de meta ou barra do gráfico) */}
      <Modal open={!!vendasModal} onCancel={() => setVendasModal(null)} footer={null}
        width={920} title={vendasModal?.titulo || ''} destroyOnClose>
        {vendasModalLoading ? (
          <Flex justify="center" style={{ padding: 40 }}><Spin /></Flex>
        ) : !vendasModalData || vendasModalData.vendas.length === 0 ? (
          <Empty description="Nenhuma venda ganha neste período" />
        ) : (
          <>
            <Flex justify="space-between" align="center" wrap="wrap" gap={8}
              style={{ marginBottom: 12, padding: '8px 12px', background: '#F0F5FF', borderRadius: 6 }}>
              <Text strong style={{ color: '#020D7F' }}>
                {vendasModalData.vendas.length} venda(s) no período
              </Text>
              <Text strong style={{ color: '#0a7f4a', fontSize: 18 }}>
                Total: {fmt(vendasModalData.total)}
              </Text>
            </Flex>
            <Table size="small" pagination={{ pageSize: 20 }} rowKey="id"
              dataSource={vendasModalData.vendas}
              columns={[
                {
                  title: 'Data', dataIndex: 'data_fechamento', key: 'd', width: 90,
                  render: (v: string) => fmtDataBr(v),
                  sorter: (a: VendaItem, b: VendaItem) =>
                    (a.data_fechamento || '').localeCompare(b.data_fechamento || ''),
                },
                {
                  title: 'Cliente', dataIndex: 'cliente', key: 'c',
                  render: (c: string | null) => c || <Text type="secondary">—</Text>,
                  ellipsis: true,
                },
                {
                  title: 'Título', dataIndex: 'titulo', key: 't', ellipsis: true,
                  render: (t: string, row: VendaItem) => (
                    <>
                      {t}
                      {row.origem === 'aditivo' && row.contrato_pai_numero && (
                        <Tag color="purple" style={{ fontSize: 9, marginLeft: 6, padding: '0 4px', lineHeight: '14px' }}>
                          Aditivo de {row.contrato_pai_numero}
                        </Tag>
                      )}
                    </>
                  ),
                },
                {
                  title: 'Origem', dataIndex: 'origem', key: 'o', width: 100,
                  render: (o: string | null) => {
                    if (!o) return <Text type="secondary">—</Text>;
                    const cor = o === 'aditivo' ? 'purple' : o === 'licitacao' ? 'blue' : 'default';
                    return <Tag color={cor} style={{ fontSize: 10 }}>{o}</Tag>;
                  },
                  filters: [
                    { text: 'Privada', value: 'privada' },
                    { text: 'Licitação', value: 'licitacao' },
                    { text: 'Aditivo', value: 'aditivo' },
                  ],
                  onFilter: (val, row: VendaItem) => row.origem === val,
                },
                {
                  title: 'Responsável', dataIndex: 'responsavel', key: 'r', width: 160,
                  ellipsis: true,
                  render: (r: string | null) => r || <Text type="secondary" italic style={{ fontSize: 11 }}>—</Text>,
                },
                {
                  title: 'Valor', dataIndex: 'valor_final', key: 'v', width: 130, align: 'right',
                  render: (v: any, row: VendaItem) => {
                    const num = parseFloat(v || row.valor_estimado || 0);
                    return <Text strong style={{ color: '#0a7f4a' }}>{fmt(num)}</Text>;
                  },
                  sorter: (a: VendaItem, b: VendaItem) =>
                    parseFloat(String(a.valor_final || 0)) - parseFloat(String(b.valor_final || 0)),
                  defaultSortOrder: 'descend',
                },
              ]} />
          </>
        )}
      </Modal>
    </div>
  );
}

function formatarMes(s: string) {
  // Aceita 'YYYY' (granularidade ano) ou 'YYYY-MM' (mês)
  if (!s) return '';
  if (/^\d{4}$/.test(s)) return s;  // só ano
  const meses = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
  const [y, m] = s.split('-');
  return `${meses[parseInt(m, 10) - 1]}/${y.slice(2)}`;
}

function tituloJanela(j: Janela): string {
  switch (j) {
    case 'mes':             return 'mês atual';
    case 'trimestre':       return 'últimos 3 meses';
    case '12m':             return 'últimos 12 meses';
    case 'ano':             return 'ano corrente';
    case 'tudo':            return 'todo o período';
    case 'ano_especifico':  return 'ano selecionado';
    case 'custom':          return 'período personalizado';
  }
}

function labelStatus(s: string) {
  const map: Record<string, string> = {
    novo: '🆕 Novo',
    qualificado: '✓ Qualificado',
    proposta: '📄 Proposta enviada',
    negociacao: '🤝 Negociação',
    ganho: '✅ Ganho',
    perdido: '❌ Perdido',
  };
  return map[s] || s;
}

export default function ComercialDashboard() {
  // COMP-ANOS (2026-07-16): pagina virou abas — "Visao geral" (conteudo
  // original intacto) + "Ano x Ano" (comparativo 2025x2026 pedido pelo Lucas).
  return (
    <AntApp>
      <Tabs
        defaultActiveKey="geral"
        destroyInactiveTabPane
        tabBarStyle={{ paddingLeft: 24, marginBottom: 0 }}
        items={[
          { key: 'geral', label: '📊 Visão geral', children: <ComercialDashboardInner /> },
          { key: 'comparativo', label: '🔁 Ano × Ano', children: <ComparativoAnos /> },
        ]}
      />
    </AntApp>
  );
}
